import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchDirectAwardDetails } from '../../services/publicTendersApi';
import './DirectAwardDetails.css';
import Utility from './../../utils/Utility';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import RestrictedAccess from '../../components/RestrictedAccess/RestrictedAccess';
import pdfIcon from '../../assets/icons/pdf-icon.png';
import docIcon from '../../assets/icons/doc-icon.png';
import xlsIcon from '../../assets/icons/xls-icon.png';
import zipIcon from '../../assets/icons/zip-icon.png';
import defaultIcon from '../../assets/icons/file-icon.png';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';

const DirectAwardDetails = () => {
  const { noticeNo } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoading(true);
        const data = await fetchDirectAwardDetails(noticeNo);
        setDetails(data);
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    getDetails();
  }, [noticeNo]);

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return pdfIcon;
      case 'doc':
      case 'docx':
        return docIcon;
      case 'xls':
      case 'xlsx':
        return xlsIcon;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
        return zipIcon;
      default:
        return defaultIcon;
    }
  };

  const handleFileDownload = async () => {
    var noticeDocumentName = details.advForView.documentName;
    var noticeDocumentUrl = details.advForView.documentUrl;

    const advNoticeId = details.advForView?.advertisingNoticeId;
    const noticeNo = details.advForView?.noticeNo;

    if (!noticeDocumentUrl || !noticeDocumentName || !advNoticeId) {
      console.error('Missing data for file download.');
      alert('Some required information is missing. Please try again.');
      return;
    }

    try {
      const url = `${
        process.env.REACT_APP_BASE_URL
      }/api/DirectAwardListWeb/download/?noticeDocumentUrl=${
        noticeDocumentUrl
      }&noticeDocumentName=${encodeURIComponent(
        noticeDocumentName,
      )}&advNoticeId=${advNoticeId}
      &noticeNo=${encodeURIComponent(noticeNo)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download the file. Please try again.');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');

      let fileName = noticeDocumentName.replace(/\.p7m|\.p7s$/, '');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error during file download:', error);
      alert('An error occurred while attempting to download the file.');
    }
  };

  if (loading) {
    return <ItemLoadingHolder />;
  }

  if (error) {
    return <p className="error-text">Eroare: {error}</p>;
  }

  if (!details) {
    return <p>Nu există date disponibile.</p>;
  }

  var labelItem =
    // `<b>${details.advForView?.noticeNo || 'N/A'}</b> ` +
    details.advForView?.contractObject || 'N/A';

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/achizitii-directe', label: 'Achiziții Directe' },
    { label: labelItem },
  ];

  return (
    <>
      <SeoPageComponent
        pageTitle={`${details.advForView?.contractObject || 'Detalii Achiziție Directă'} - ${process.env.REACT_APP_NAME}`}
        pageDescription={`Informații despre achiziția directă ${details.stickyData?.contractTitle || 'N/A'}, publicată de ${details.stickyData?.caName || 'N/A'}.`}
        pageUrl={`${process.env.REACT_APP_DOMAIN}/achizitii-directe/${noticeNo}`}
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

      {/* Titlu complet, vizibil */}
      <section className="pt-10 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold text-indigo-700 text-center mb-4 break-words drop-shadow-xl">
            {details.advForView?.contractObject || 'Detalii Achiziție Directă'}
          </h1>
        </div>
      </section>

      {/* Summary bar compact, smart, vizibil rapid */}
      <section className="pb-6 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-2 md:gap-4 bg-white/80 rounded-xl shadow-lg px-4 py-4 md:py-6 mb-6 border border-indigo-100">
            <div className="flex flex-wrap gap-2 justify-center items-center w-full md:w-auto">
              <span className="bg-green-100 text-green-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-green-200 shadow-sm">
                {details.advForView?.noticeNo || 'N/A'}
              </span>
              <span className="bg-yellow-100 text-yellow-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-yellow-200 shadow-sm">
                {details.advForView?.contractAuthorityName || 'N/A'}
              </span>
              <span className="bg-purple-100 text-purple-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-purple-200 shadow-sm">
                {details.advForView?.awardCriteria || 'N/A'}
              </span>
              <span className="bg-indigo-100 text-indigo-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {Utility.ConvertEstimatedValue(
                  details.advForView?.estimatedValue,
                )}{' '}
                {details.advForView?.currency?.text || 'RON'}
              </span>
              {details.advForView?.cpvCode?.text && (
                <span className="bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                  CPV: {details.advForView.cpvCode.text}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Detalii compacte, organizate pe grid modern */}
      <section className="pb-16 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Info cheie */}
            <div className="bg-white shadow rounded-xl p-6 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-lg font-bold mb-2">
                Detalii Achiziție
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Titlu:</span>
                  <span
                    className="truncate"
                    title={details.advForView?.contractObject}
                  >
                    {details.advForView?.contractObject || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Număr:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Numar licitatie disponibil doar pentru utilizatorii autentificati"
                  >
                    <span className="text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded text-sm">
                      {details.advForView?.noticeNo || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Autoritate:</span>
                  <span className="text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded text-sm">
                    {details.advForView?.contractAuthorityName || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Criterii atribuire:</span>
                  <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded text-sm">
                    {details.advForView?.awardCriteria || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {/* Fișiere disponibile */}
            <div className="bg-white shadow rounded-xl p-6 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-lg font-bold mb-2">
                Fișiere Disponibile
              </h2>
              <RestrictedAccess message="Sectiunea fisiere disponibila doar pentru utilizatorii autentificati">
                <div className="space-y-2">
                  {details.advForView?.documentUrl &&
                  details.advForView?.documentName ? (
                    <div className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50 hover:shadow transition">
                      <img
                        src={getFileIcon(
                          details.advForView.documentName
                            .split('.')
                            .pop()
                            .toLowerCase(),
                        )}
                        alt="File icon"
                        className="w-6 h-6"
                      />
                      <button
                        onClick={handleFileDownload}
                        className="text-indigo-600 font-semibold hover:underline text-sm truncate"
                      >
                        {details.advForView.documentName}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Nu există fișiere disponibile.
                    </p>
                  )}
                </div>
              </RestrictedAccess>
            </div>
            {/* Valori financiare, termene, CPV */}
            <div className="flex flex-col gap-6">
              <div className="bg-white shadow rounded-xl p-6 flex flex-col gap-2">
                <h2 className="text-indigo-500 text-lg font-bold mb-2">
                  Valori Financiare
                </h2>
                <div className="flex items-center gap-2 text-base text-gray-700">
                  <span className="font-medium">Valoare Estimată:</span>
                  <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">
                    {Utility.ConvertEstimatedValue(
                      details.advForView?.estimatedValue,
                    )}{' '}
                    {details.advForView?.currency?.text || 'RON'}
                  </span>
                </div>
              </div>
              <div className="bg-white shadow rounded-xl p-6 flex flex-col gap-2">
                <h2 className="text-indigo-500 text-lg font-bold mb-2">
                  Termene
                </h2>
                <div className="flex flex-col gap-1 text-base text-gray-700">
                  <span>
                    <span className="font-medium">Data publicării:</span>{' '}
                    {Utility.FormatDate(details.advForView?.publicationDate) ||
                      'N/A'}
                  </span>
                  <span>
                    <span className="font-medium">Termen Limită:</span>{' '}
                    {Utility.FormatDate(
                      details.advForView?.tenderReceiptDeadline,
                    ) || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="bg-white shadow rounded-xl p-6 flex flex-col gap-2">
                <h2 className="text-indigo-500 text-lg font-bold mb-2">
                  Coduri CPV
                </h2>
                <span className="font-medium">Cod CPV:</span>{' '}
                <span>{details.advForView?.cpvCode?.text || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Descriere contract */}
          {details.advForView?.contractDescription && (
            <div className="bg-white shadow rounded-xl p-6 group mt-8">
              <h2 className="text-indigo-500 text-lg font-bold mb-2">
                Descriere Contract
              </h2>
              <p className="text-base text-gray-700">
                {details.advForView.contractDescription}
              </p>
            </div>
          )}

          {/* Conditii de participare */}
          <div className="bg-white shadow rounded-xl p-6 group mt-8">
            <h2 className="text-indigo-500 text-lg font-bold mb-2">
              Conditii de Participare
            </h2>
            <p className="text-base text-gray-700">
              {details.advForView.participationConditions}
            </p>
          </div>

          {/* Informatii aditionale */}
          <div className="bg-white shadow rounded-xl p-6 group mt-8">
            <h2 className="text-indigo-500 text-lg font-bold mb-2">
              Informatii Aditionale
            </h2>
            <p className="text-base text-gray-700">
              {details.advForView.additionalInformation}
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default DirectAwardDetails;
